/** @jsxImportSource @emotion/react */

import React, { JSX, Children, ReactNode } from 'react';
import { breakpoints } from '../../../global/global-config';
import {
  Accordion as SherpaAccordion,
  AccordionItemDivider as SherpaAccordionItemDivider,
  AccordionItemProps as SherpaAccordionItemProps,
} from '../../../../deps/@bshgroup/component-accordion';
import { AccordionControlProvider } from './AccordionControlContext';
import { CSSProp } from '../../../types/emotion';

export interface AccordionProps {
  /**
   * Only have one item opened at a time
   */
  single?: boolean;
  /**
   * Whether to scroll the item into view when opened
   */
  scrollIntoView?: boolean;
  /**
   * Accordion items
   */
  children: ReactNode;
}

/**
 * The `Accordion` displays a list of high-level options that can expand/collapse to reveal more information.
 */
export function Accordion({
  single = false,
  scrollIntoView = false,
  children,
  ...props
}: AccordionProps & Omit<Partial<React.HTMLAttributes<HTMLDivElement>>, keyof AccordionProps> & CSSProp) {
  return (
    <AccordionControlProvider
      initialState={
        children &&
        Object.fromEntries(
          Children.toArray(children)
            .map((child: any) => [child?.props?.id, child?.props?.initialExpanded ?? false])
            .filter(e => e[1]),
        )
      }
      single={single}
      scrollIntoView={scrollIntoView}
    >
      <SherpaAccordion
        size={{
          [breakpoints.sm]: 'sm',
          [breakpoints.md]: 'md',
        }}
        items={
          React.Children.map(children, child => {
            return () => child;
          }) as ((config: Pick<SherpaAccordionItemProps, 'size'>) => JSX.Element)[]
        }
        divider={config => <SherpaAccordionItemDivider {...config} />}
        {...props}
      />
    </AccordionControlProvider>
  );
}
